import React, { useEffect, useState } from 'react'
import * as tus from 'tus-js-client'
import { API_BASE_URL } from '../data'
import { Training, TrainingContent, TrainingFormModalMetaProps, TrainingModule, TrainingQuestion } from '../data/types'
import {
    AudioIcon,
    DeleteIcon,
    DocumentIcon,
    ExternalLinkIcon,
    InteractiveIcon,
    ConstructionIcon,
    PlusIcon,
    QuizIcon,
    UploadIcon,
    VideoIcon
} from '../icons'
import { EditableList } from './ui'
import { Button } from './ui/Button'
import { Modal } from './ui/Modal'

type Props = {
    meta: TrainingFormModalMetaProps
    isOpen: boolean
    onClose: () => void
    onSubmit: (trainingData: any) => Promise<void>
    initialTraining?: Training | null
}

const getModuleIcon = (type: string, className?: string) => {
    switch (type) {
        case 'video':
            return <VideoIcon className={className} />
        case 'document':
            return <DocumentIcon className={className} />
        case 'audio':
            return <AudioIcon className={className} />
        case 'quiz':
            return <QuizIcon className={className} />
        case 'interactive':
            return <InteractiveIcon className={className} />
        case 'external':
            return <ExternalLinkIcon className={className} />
        case 'coming_soon':
            return <ConstructionIcon className={className} />
        default:
            return <DocumentIcon className={className} />
    }
}

type TrainingModuleWithExtras = TrainingModule & {
    contentType?: 'url' | 'file' | 'library' | 'scorm'
    _id?: string
    questions: (TrainingQuestion & { _id?: string })[]
    scorm?: {
        launch_url: string
        version: '1.2'
    }
}


const emptyModule: TrainingModuleWithExtras = {
    type: 'video',
    title: '',
    url: '',
    description: '',
    duration: undefined,
    required: true,
    questions: [],
    contentType: 'url'
}

// Create an empty question Model
const emptyQuestion: TrainingQuestion & { _id?: string } = {
    text: '',
    options: [],
    answer: 0,
    answer_description: ''
}




export const TrainingFormModal = (props: Props) => {
    const { meta, isOpen, onClose, onSubmit, initialTraining } = props

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        mandatory: false,
        policy: false
    })

    const [modules, setModules] = useState<TrainingModuleWithExtras[]>([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [uploadingFiles, setUploadingFiles] = useState<Set<number>>(new Set())
    const [uploadProgress, setUploadProgress] = useState<Map<number, number>>(new Map())
    const [tusUploads, setTusUploads] = useState<Map<number, tus.Upload>>(new Map())
    const [showLibraryBrowser, setShowLibraryBrowser] = useState(false)
    const [currentModuleIndex, setCurrentModuleIndex] = useState<number | null>(null)
    const [libraryVideos, setLibraryVideos] = useState<any[]>([])
    const [loadingLibrary, setLoadingLibrary] = useState(false)

    // Populate form when editing a training
    useEffect(() => {
        if (initialTraining && isOpen) {
            setFormData({
                name: initialTraining.name,
                description: initialTraining.description,
                mandatory: initialTraining.mandatory || false,
                policy: initialTraining.policy || false
            })

            // Parse content to get modules
            let parsedContent: TrainingContent | null = null
            if (typeof initialTraining.content === 'string') {
                try {
                    parsedContent = JSON.parse(initialTraining.content) as TrainingContent
                } catch {
                    parsedContent = null
                }
            } else {
                parsedContent = initialTraining.content
            }

            // Add contentType to modules based on whether they have url or file_key
            const modulesWithContentType = (parsedContent?.modules || []).map((module, idx) => ({
                ...module,
                questions: module.questions?.map((question, qIdx) => ({
                    ...question,
                    _id: `question-${Date.now()}-${qIdx}` // Generate ID for existing questions
                })) || [],
                contentType: module.file_key
                    ? (module.file_key.startsWith('bunny:') ? 'library' : 'file')
                    : 'url',
                _id: `module-${Date.now()}-${idx}` // Generate ID for existing modules
            })) as (typeof emptyModule)[]

            setModules(modulesWithContentType)
        } else if (!isOpen) {
            // Reset form when modal closes
            setFormData({
                name: '',
                description: '',
                mandatory: false,
                policy: false
            })
            setModules([])
            setError(null)
        }
    }, [initialTraining, isOpen])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target
        const checked = (e.target as HTMLInputElement).checked
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }))
    }

    const handleAddOption = (moduleIndex: number, questionIndex: number) => {
        const module = { ...modules[moduleIndex] };
        module.questions = [...module.questions || []];
        module.questions[questionIndex] = { ...module.questions[questionIndex], options: [...(module.questions[questionIndex].options), ''] };
        const newModules = [...modules];
        newModules[moduleIndex] = module;
        setModules(newModules);
    };

    const handleRemoveOption = (moduleIndex: number, questionIndex: number, optionIndex: number) => {
        const module = { ...modules[moduleIndex] };
        module.questions = [...module.questions || []];
        module.questions[questionIndex] = {
            ...module.questions[questionIndex],
            options: module.questions[questionIndex].options.filter((_, i) => i !== optionIndex)
        };
        const newModules = [...modules];
        newModules[moduleIndex] = module;
        setModules(newModules);
    }

    const handleModuleChange = (index: number, field: keyof TrainingModule | 'contentType' | 'scorm', value: any) => {
        const newModules = [...modules]
        newModules[index] = { ...newModules[index], [field]: value }
        setModules(newModules)
    }

    const handleQuestionChange = (moduleIndex: number, index: number, field: keyof TrainingQuestion, value: any) => {
        handleModuleChange(moduleIndex, 'questions', [...(modules[moduleIndex].questions || []).map((q, i) => i === index ? { ...q, [field]: value } : q)])
    }

    const handleOptionChange = (
        moduleIndex: number,
        questionIndex: number,
        optionIndex: number,
        value: string
    ) => {
        handleQuestionChange(
            moduleIndex,
            questionIndex,
            'options',
            [...(modules[moduleIndex].questions || [])[questionIndex].options.map((opt, i) => i === optionIndex ? value : opt)]
        )
    }

    const handleContentTypeChange = (index: number, contentType: 'url' | 'file' | 'library') => {
        const newModules = [...modules]
        if (contentType === 'file') {
            newModules[index] = { ...newModules[index], contentType: 'file', url: '' }
        } else if (contentType === 'library') {
            newModules[index] = { ...newModules[index], contentType: 'library', url: '' }
        } else {
            newModules[index] = { ...newModules[index], contentType: 'url', file_key: undefined }
        }
        setModules(newModules)
    }

    const handleFileUpload = async (index: number, file: File) => {
        try {
            setUploadingFiles(new Set(Array.from(uploadingFiles).concat(index)))
            setError(null)

            // Step 1: Get presigned URL from backend
            const filename = file.name
            const response = await fetch(`${API_BASE_URL}/file`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({ filename, folder: 'trainings' })
            })

            if (!response.ok) {
                const errorData = await response.json().catch(() => null)
                console.error('File upload error:', { status: response.status, errorData })
                const errorMessage = errorData?.message || errorData?.error || 'Failed to get upload URL'
                throw new Error(errorMessage)
            }

            const result = await response.json()
            const { url: presignedUrl, key } = result.data || result

            // Step 2: Upload file to storage using presigned URL
            const uploadResponse = await fetch(presignedUrl, {
                method: 'PUT',
                body: file,
                headers: {
                    'Content-Type': file.type,
                    'Accept': 'application/json'
                }
            })

            if (!uploadResponse.ok) {
                throw new Error('Failed to upload file')
            }

            // Step 3: Update module with file_key
            const newModules = [...modules]
            newModules[index] = {
                ...newModules[index],
                file_key: key,
                url: undefined // Clear URL when file is uploaded
            }
            setModules(newModules)
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to upload file')
        } finally {
            setUploadingFiles(prev => {
                const newSet = new Set(Array.from(prev))
                newSet.delete(index)
                return newSet
            })
        }
    }

    const handleRemoveFile = (index: number) => {
        // Cancel ongoing upload if exists
        const upload = tusUploads.get(index)
        if (upload) {
            upload.abort()
            tusUploads.delete(index)
            setTusUploads(new Map(tusUploads))
        }

        const newModules = [...modules]
        newModules[index] = {
            ...newModules[index],
            file_key: undefined
        }
        setModules(newModules)

        // Clear progress
        const newProgress = new Map(uploadProgress)
        newProgress.delete(index)
        setUploadProgress(newProgress)
    }

    const handleVideoUpload = async (index: number, file: File) => {
        try {
            setUploadingFiles(new Set(Array.from(uploadingFiles).concat(index)))
            setError(null)

            // Extract video duration from the file
            let videoDuration: number | undefined = undefined
            try {
                videoDuration = await new Promise<number>((resolve, reject) => {
                    const video = document.createElement('video')
                    video.preload = 'metadata'
                    video.onloadedmetadata = () => {
                        window.URL.revokeObjectURL(video.src)
                        // Convert to minutes and round up
                        const durationInMinutes = Math.ceil(video.duration / 60)
                        resolve(durationInMinutes)
                    }
                    video.onerror = () => {
                        window.URL.revokeObjectURL(video.src)
                        reject(new Error('Failed to load video metadata'))
                    }
                    video.src = URL.createObjectURL(file)
                })
                console.log('Video duration extracted:', videoDuration, 'minutes')
            } catch (err) {
                console.warn('Could not extract video duration:', err)
                // Continue with upload even if duration extraction fails
            }

            // Create video object and get TUS credentials from backend
            const response = await fetch(`${API_BASE_URL}/video`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({
                    title: file.name.replace(/\.[^/.]+$/, '') // Remove file extension
                }),
                credentials: 'include'
            })

            if (!response.ok) {
                const errorData = await response.json().catch(() => null)
                console.error('Video creation error:', { status: response.status, errorData })
                const errorMessage = errorData?.message || errorData?.error || 'Failed to create video'
                throw new Error(errorMessage)
            }

            const result = await response.json()
            const {
                video_id,
                library_id,
                tus_endpoint,
                authorization_signature,
                authorization_expire
            } = result.data || result


            const upload = new tus.Upload(file, {
                endpoint: tus_endpoint,
                retryDelays: [0, 3000, 5000, 10000, 20000],
                metadata: {
                    filename: file.name,
                    filetype: file.type,
                    videoId: video_id,
                    libraryId: library_id
                },
                headers: {
                    'AuthorizationSignature': authorization_signature,
                    'AuthorizationExpire': authorization_expire.toString(),
                    'VideoId': video_id,
                    'LibraryId': library_id
                },
                onError: (error) => {
                    console.error('TUS upload failed:', error)
                    setError(`Upload failed: ${error.message}`)
                    setUploadingFiles(prev => {
                        const newSet = new Set(Array.from(prev))
                        newSet.delete(index)
                        return newSet
                    })
                    // Clear progress
                    const newProgress = new Map(uploadProgress)
                    newProgress.delete(index)
                    setUploadProgress(newProgress)
                },
                onProgress: (bytesUploaded, bytesTotal) => {
                    const percentage = Math.round((bytesUploaded / bytesTotal) * 100)
                    const newProgress = new Map(uploadProgress)
                    newProgress.set(index, percentage)
                    setUploadProgress(newProgress)
                },
                onSuccess: () => {
                    console.log('Upload completed! Video ID:', video_id)
                    // Update module with video_id as file_key and duration
                    const newModules = [...modules]
                    newModules[index] = {
                        ...newModules[index],
                        file_key: `bunny:${video_id}`, // Prefix to identify bunny.net videos
                        url: undefined, // Clear URL when video is uploaded
                        duration: videoDuration // Set the extracted duration
                    }
                    setModules(newModules)

                    setUploadingFiles(prev => {
                        const newSet = new Set(Array.from(prev))
                        newSet.delete(index)
                        return newSet
                    })

                    // Clear progress after short delay
                    setTimeout(() => {
                        const newProgress = new Map(uploadProgress)
                        newProgress.delete(index)
                        setUploadProgress(newProgress)
                    }, 2000)

                    // Remove from uploads map
                    tusUploads.delete(index)
                    setTusUploads(new Map(tusUploads))
                }
            })

            // Store upload instance for potential cancellation
            tusUploads.set(index, upload)
            setTusUploads(new Map(tusUploads))

            // Start upload
            upload.start()
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to upload video')
            setUploadingFiles(prev => {
                const newSet = new Set(Array.from(prev))
                newSet.delete(index)
                return newSet
            })
        }
    }

    const fetchLibraryVideos = async () => {
        try {
            setLoadingLibrary(true)
            setError(null)

            const response = await fetch(`${API_BASE_URL}/video`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include'
            })

            if (!response.ok) {
                throw new Error('Failed to fetch library videos')
            }

            const result = await response.json()
            // Handle nested data structure: result.data.items
            const videos = result.data?.items || result.data || result

            setLibraryVideos(Array.isArray(videos) ? videos : [])
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load library videos')
        } finally {
            setLoadingLibrary(false)
        }
    }

    const handleSelectLibraryVideo = (video: any) => {
        if (currentModuleIndex === null) return

        const newModules = [...modules]
        const videoId = video.guid || video.id || video.video_id

        newModules[currentModuleIndex] = {
            ...newModules[currentModuleIndex],
            file_key: `bunny:${videoId}`,
            contentType: 'library',
            duration: video.length ? Math.ceil(video.length / 60) : newModules[currentModuleIndex].duration // Convert seconds to minutes
        }

        setModules(newModules)
        setShowLibraryBrowser(false)
        setCurrentModuleIndex(null)
    }

    const handleBrowseLibrary = (index: number) => {
        setCurrentModuleIndex(index)
        setShowLibraryBrowser(true)
        fetchLibraryVideos()
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            // Validate that we have at least one module
            if (modules.length === 0) {
                throw new Error("Please add at least one module to the training");
            }

            // Validate required module fields
            for (let i = 0; i < modules.length; i++) {
                const module = modules[i];
                if (!module.title.trim()) {
                    throw new Error(`Module ${i + 1}: Title is required`);
                }
                if ((module.type === 'document' || module.type === 'video') && !module.url && !module.file_key) {
                    throw new Error(`Module '${module.title}': Either URL or file upload is required`);
                }

                if (module.type === 'scorm' && !module.scorm?.launch_url) {
                    throw new Error(`Module '${module.title}': SCORM launch URL is required`)
                }

                const questions = module.questions || [];
                if (questions.length > 0) {
                    for (let j = 0; j < questions.length; j++) {
                        if (!questions[j].text.trim()) {
                            throw new Error(`Module '${module.title}': Question ${j + 1} text is required.`);
                        }
                        if (questions[j].options.length < 2) {
                            throw new Error(`Module '${module.title}': Question ${j + 1} must have at least 2 options.`);
                        }
                        const hasOptionText = questions[j].options.every((option) => {
                            return option.trim()
                        })

                        if (!hasOptionText) {
                            throw new Error(`Module '${module.title}': All options must have text in question ${j + 1}.`);
                        }
                    }
                }
            }

            // Clean modules before saving
            const cleanedModules = modules.map(module => {
                const cleaned: any = {
                    type: module.type,
                    title: module.title
                };

                // Add either url or file_key
                if (module.file_key) cleaned.file_key = module.file_key;
                else if (module.url) cleaned.url = module.url;

                if (module.description?.trim()) cleaned.description = module.description;
                if (module.duration) cleaned.duration = module.duration;
                if (module.required !== undefined) cleaned.required = module.required;

                // Clean Scorm
                if (module.type === 'scorm') {
                    cleaned.scorm = module.scorm
                }

                // Clean questions (remove empty options, etc.)
                const cleanedQuestions = module.questions?.map((q, qIndex) => ({
                    text: q.text,
                    options: q.options.map((opt, i) => opt.trim()),
                    answer: q.answer,
                    answer_description: q.answer_description
                }));

                if (cleanedQuestions && cleanedQuestions.length > 0) {
                    cleaned.questions = cleanedQuestions;
                }

                // Note: _id and contentType are not saved to backend
                return cleaned;
            });

            // FINAL CONTENT OBJECT (modules + questions)
            const content = JSON.stringify({
                modules: cleanedModules
            });

            const submissionData = {
                ...formData,
                content
            };

            await onSubmit(submissionData);
            onClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : meta.errorMessage);
        } finally {
            setLoading(false);
        }
    };


    const footer = (
        <div className="flex justify-end gap-3">
            <Button
                type="button"
                onClick={onClose}
                variant="outline"
                color="neutral"
                disabled={loading}
            >
                {meta.cancelButton}
            </Button>
            <Button
                type="submit"
                onClick={handleSubmit}
                color="primary"
                disabled={loading}
            >
                {loading ? meta.submittingButton : meta.submitButton}
            </Button>
        </div>
    )

    return (
        <>
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                title={meta.title}
                footer={footer}
                maxWidth="2xl"
            >
                <form onSubmit={handleSubmit} className="px-6 py-4">
                    {error && (
                        <div className="mb-4 bg-danger-100 dark:bg-danger-900 border border-danger-400 dark:border-danger-700 text-danger-700 dark:text-danger-200 px-4 py-3 rounded-lg">
                            <p>{error}</p>
                        </div>
                    )}

                    <div className="space-y-6">
                        {/* Name Field */}
                        <div>
                            <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                {meta.nameLabel}
                            </label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                placeholder={meta.namePlaceholder}
                                required
                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            />
                        </div>

                        {/* Description Field */}
                        <div>
                            <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                {meta.descriptionLabel}
                            </label>
                            <textarea
                                id="description"
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                placeholder={meta.descriptionPlaceholder}
                                required
                                rows={3}
                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            />
                        </div>

                        {/* Mandatory Checkbox */}
                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                id="mandatory"
                                name="mandatory"
                                checked={formData.mandatory}
                                onChange={handleChange}
                                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                            />
                            <label htmlFor="mandatory" className="ms-2 block text-sm text-gray-700 dark:text-gray-300">
                                {meta.mandatoryLabel}
                            </label>
                        </div>

                        {/* Policy Checkbox */}
                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                id="policy"
                                name="policy"
                                checked={formData.policy}
                                onChange={handleChange}
                                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                            />
                            <label htmlFor="policy" className="ms-2 block text-sm text-gray-700 dark:text-gray-300">
                                {meta.policyLabel}
                            </label>
                        </div>

                        {/* Modules Section */}
                        <EditableList
                            items={modules}
                            onItemsChange={setModules}
                            getItemId={(module) => module._id}
                            createNewItem={() => ({ ...emptyModule, _id: `module-${Date.now()}-${Math.random()}` })}
                            renderItem={(module, moduleIndex) => (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {/* Module Type */}
                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            {meta.moduleTypeLabel}
                                        </label>
                                        <select
                                            value={module.type}
                                            onChange={(e) => handleModuleChange(moduleIndex, 'type', e.target.value)}
                                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                        >
                                            <option value="video">Video</option>
                                            <option value="document">Document</option>
                                            <option value="audio">Audio</option>
                                            <option value="scorm">SCORM</option>
                                            <option value="quiz">Quiz</option>
                                            <option value="interactive">Interactive</option>
                                            <option value="external">External Link</option>
                                            <option value="coming_soon">Coming Soon</option>
                                        </select>
                                    </div>

                                    {/* Module Title */}
                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            {meta.moduleTitleLabel} *
                                        </label>
                                        <input
                                            type="text"
                                            value={module.title}
                                            onChange={(e) => handleModuleChange(moduleIndex, 'title', e.target.value)}
                                            placeholder={meta.moduleTitlePlaceholder}
                                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                        />
                                    </div>

                                    {/* URL or File Upload - For document and video types, but not coming_soon */}
                                    {(module.type === 'document' || module.type === 'video') ? (
                                        <div className="md:col-span-2 space-y-3">
                                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                {meta.moduleContentTypeLabel} *
                                            </label>
                                            <div className="flex gap-4 flex-wrap">
                                                <label className="flex items-center gap-2 cursor-pointer">
                                                    <input
                                                        type="radio"
                                                        name={`contentType-${moduleIndex}`}
                                                        value="url"
                                                        checked={(module.contentType || 'url') === 'url'}
                                                        onChange={() => handleContentTypeChange(moduleIndex, 'url')}
                                                        className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 focus:ring-primary-500"
                                                    />
                                                    <span className="text-sm text-gray-700 dark:text-gray-300">
                                                        {meta.moduleContentTypeUrl}
                                                    </span>
                                                </label>
                                                <label className="flex items-center gap-2 cursor-pointer">
                                                    <input
                                                        type="radio"
                                                        name={`contentType-${moduleIndex}`}
                                                        value="file"
                                                        checked={module.contentType === 'file'}
                                                        onChange={() => handleContentTypeChange(moduleIndex, 'file')}
                                                        className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 focus:ring-primary-500"
                                                    />
                                                    <span className="text-sm text-gray-700 dark:text-gray-300">
                                                        {meta.moduleContentTypeFile}
                                                    </span>
                                                </label>
                                                {module.type === 'video' && (
                                                    <label className="flex items-center gap-2 cursor-pointer">
                                                        <input
                                                            type="radio"
                                                            name={`contentType-${moduleIndex}`}
                                                            value="library"
                                                            checked={module.contentType === 'library'}
                                                            onChange={() => handleContentTypeChange(moduleIndex, 'library')}
                                                            className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 focus:ring-primary-500"
                                                        />
                                                        <span className="text-sm text-gray-700 dark:text-gray-300">
                                                            {meta.moduleContentTypeLibrary}
                                                        </span>
                                                    </label>
                                                )}
                                            </div>

                                            {(module.contentType || 'url') === 'url' ? (
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                        {meta.moduleUrlLabel} *
                                                    </label>
                                                    <input
                                                        type="url"
                                                        value={module.url || ''}
                                                        onChange={(e) => handleModuleChange(moduleIndex, 'url', e.target.value)}
                                                        placeholder={meta.moduleUrlPlaceholder}
                                                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                                    />
                                                </div>
                                            ) : module.contentType === 'library' ? (
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                        {meta.moduleFileLabel} *
                                                    </label>
                                                    {module.file_key ? (
                                                        <div className="flex items-center gap-2">
                                                            <div className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 truncate">
                                                                Video: {module.file_key.substring(6)}
                                                            </div>
                                                            <Button
                                                                type="button"
                                                                onClick={() => handleRemoveFile(moduleIndex)}
                                                                variant="outline"
                                                                color="danger"
                                                                size="sm"
                                                            >
                                                                {meta.moduleFileRemoveButton}
                                                            </Button>
                                                        </div>
                                                    ) : (
                                                        <Button
                                                            type="button"
                                                            onClick={() => handleBrowseLibrary(moduleIndex)}
                                                            variant="outline"
                                                            size="sm"
                                                            className="w-full"
                                                        >
                                                            <VideoIcon className="w-4 h-4" />
                                                            {meta.browseLibraryButton}
                                                        </Button>
                                                    )}
                                                </div>
                                            ) : (
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                        {meta.moduleFileLabel} *
                                                    </label>
                                                    {module.file_key ? (
                                                        <div className="flex items-center gap-2">
                                                            <div className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 truncate">
                                                                {module.file_key.startsWith('bunny:') ? 'Video: ' + module.file_key.substring(6) : module.file_key.split('/').pop()}
                                                            </div>
                                                            <Button
                                                                type="button"
                                                                onClick={() => handleRemoveFile(moduleIndex)}
                                                                variant="outline"
                                                                color="danger"
                                                                size="sm"
                                                            >
                                                                {meta.moduleFileRemoveButton}
                                                            </Button>
                                                        </div>
                                                    ) : (
                                                        <div className="space-y-2">
                                                            <div className="flex items-center gap-2">
                                                                <input
                                                                    type="file"
                                                                    id={`file-${moduleIndex}`}
                                                                    accept={module.type === 'video' ? 'video/*' : 'application/pdf,text/html,.html,.htm'}
                                                                    onChange={(e) => {
                                                                        const file = e.target.files?.[0]
                                                                        if (file) {
                                                                            if (module.type === 'video') {
                                                                                handleVideoUpload(moduleIndex, file)
                                                                            } else {
                                                                                handleFileUpload(moduleIndex, file)
                                                                            }
                                                                        }
                                                                    }}
                                                                    className="hidden"
                                                                />
                                                                <label
                                                                    htmlFor={`file-${moduleIndex}`}
                                                                    className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600"
                                                                >
                                                                    {meta.moduleFilePlaceholder}
                                                                </label>
                                                                <Button
                                                                    type="button"
                                                                    onClick={() => document.getElementById(`file-${moduleIndex}`)?.click()}
                                                                    variant="outline"
                                                                    size="sm"
                                                                    disabled={uploadingFiles.has(moduleIndex)}
                                                                >
                                                                    <UploadIcon className="w-4 h-4" />
                                                                    {uploadingFiles.has(moduleIndex) ? meta.moduleFileUploadingButton : meta.moduleFileUploadButton}
                                                                </Button>
                                                            </div>
                                                            {uploadingFiles.has(moduleIndex) && uploadProgress.has(moduleIndex) && (
                                                                <div className="w-full">
                                                                    <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400 mb-1">
                                                                        <span>Uploading...</span>
                                                                        <span>{uploadProgress.get(moduleIndex)}%</span>
                                                                    </div>
                                                                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                                                        <div
                                                                            className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                                                                            style={{ width: `${uploadProgress.get(moduleIndex)}%` }}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        module.type === 'scorm' ? (
                                            <div className="md:col-span-2">
                                                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                    SCORM Launch URL *
                                                </label>

                                                <input
                                                    type="url"
                                                    required
                                                    placeholder="http://localhost:8000/story.html"
                                                    value={module.scorm?.launch_url || ''}
                                                    onChange={(e) =>
                                                        handleModuleChange(moduleIndex, 'scorm', {
                                                            launch_url: e.target.value,
                                                            version: '1.2'
                                                        })
                                                    }
                                                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg"
                                                />

                                                <p className="text-xs text-gray-500 mt-1">
                                                    Upload SCORM package to S3 or local server and paste story.html URL
                                                </p>
                                            </div>
                                        ) : (< div className="md:col-span-2">
                                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                {meta.moduleUrlLabel} *
                                            </label>
                                            <input
                                                type="url"
                                                value={module.url || 'No content'}
                                                disabled
                                                readOnly
                                                onChange={(e) => handleModuleChange(moduleIndex, 'url', e.target.value)}
                                                placeholder={meta.moduleUrlPlaceholder}
                                                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 opacity-50"
                                            />
                                        </div>)


                                    )}

                                    {/* Description */}
                                    <div className="md:col-span-2">
                                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            {meta.moduleDescriptionLabel}
                                        </label>
                                        <textarea
                                            value={module.description || ''}
                                            onChange={(e) => handleModuleChange(moduleIndex, 'description', e.target.value)}
                                            placeholder={meta.moduleDescriptionPlaceholder}
                                            rows={2}
                                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                        />
                                    </div>

                                    {/* Required Checkbox */}
                                    <div className="md:col-span-2">
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={module.required ?? true}
                                                onChange={(e) => handleModuleChange(moduleIndex, 'required', e.target.checked)}
                                                className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500 dark:focus:ring-primary-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                                            />
                                            <span className="text-sm text-gray-700 dark:text-gray-300">
                                                {meta.moduleRequiredLabel}
                                            </span>
                                        </label>
                                    </div>

                                    {/* Duration */}
                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            {meta.moduleDurationLabel}
                                        </label>
                                        <input
                                            type="number"
                                            value={module.duration || ''}
                                            onChange={(e) => handleModuleChange(moduleIndex, 'duration', e.target.value ? parseInt(e.target.value) : undefined)}
                                            placeholder={meta.moduleDurationPlaceholder}
                                            min="1"
                                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                        />
                                    </div>

                                    {/* Questions */}
                                    <EditableList
                                        className='md:col-span-2'
                                        items={module.questions}
                                        onItemsChange={(updatedQuestions) => handleModuleChange(moduleIndex, 'questions', updatedQuestions)}
                                        getItemId={(question) => question._id || `question-${Math.random()}`}
                                        createNewItem={() => ({ ...emptyQuestion, _id: `question-${Date.now()}-${Math.random()}` })}
                                        renderHeader={(question, questionIndex) => (
                                            <span className="font-medium text-gray-900 dark:text-white">
                                                Question {questionIndex + 1}
                                            </span>
                                        )}
                                        renderItem={(question, questionIndex) => (
                                            <div>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                                                    {/* Question Area */}
                                                    <div className="col-span-1 md:col-span-2">
                                                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-3">
                                                            {meta.questionLable} *
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={question.text}
                                                            onChange={(e) => handleQuestionChange(moduleIndex, questionIndex, 'text', e.target.value)}
                                                            placeholder={meta.questionPlaceHolder}
                                                            required
                                                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg
                                                        focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400
                                                        focus:border-transparent bg-white dark:bg-gray-700
                                                        text-gray-900 dark:text-gray-100"
                                                        />
                                                    </div>

                                                    {/* Answer Description Area */}
                                                    <div className="col-span-1 md:col-span-2">
                                                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-3">
                                                            {meta.answerDescriptionLabel}
                                                        </label>
                                                        <textarea
                                                            value={question.answer_description || ''}
                                                            onChange={(e) => handleQuestionChange(moduleIndex, questionIndex, 'answer_description', e.target.value)}
                                                            placeholder={meta.answerDescriptionPlaceholder}
                                                            rows={2}
                                                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg
                                                        focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400
                                                        focus:border-transparent bg-white dark:bg-gray-700
                                                        text-gray-900 dark:text-gray-100"
                                                        />
                                                    </div>
                                                </div>
                                                {/* Options Area */}

                                                <div className="flex items-center justify-between mb-4 mt-6">
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                        {meta.optionsLabel} *
                                                    </label>
                                                    <Button
                                                        type="button"
                                                        onClick={() => handleAddOption(moduleIndex, questionIndex)}
                                                        variant="outline"
                                                        size="sm"
                                                    >
                                                        <PlusIcon className="w-4 h-4" />
                                                        {meta.addOptionButton}
                                                    </Button>
                                                </div>
                                                {question.options.length === 0 ? (
                                                    <div className="text-center py-8 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
                                                        <p className="text-gray-500 dark:text-gray-400">{meta.noOptionMessage}</p>
                                                    </div>
                                                ) :
                                                    <div className="space-y-4 max-h-[400px] overflow-y-auto pe-2">
                                                        <span><p className="text-gray-500 dark:text-gray-400 text-sm">{meta.optionSelectionMessage}</p></span>
                                                        {question.options.map((option, index) => (
                                                            <div
                                                                key={index}
                                                                className="flex items-center gap-3 mb-3 bg-gray-50 dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700"
                                                            >
                                                                {/* Radio Button */}
                                                                <input
                                                                    type="radio"
                                                                    name={`answer-${index}-${questionIndex}`}
                                                                    checked={question.answer === index}
                                                                    onChange={() => handleQuestionChange(moduleIndex, questionIndex, "answer", index)}
                                                                />

                                                                {/* Text Input */}
                                                                <input
                                                                    type="text"
                                                                    value={option}
                                                                    required
                                                                    onChange={(e) =>
                                                                        handleOptionChange(moduleIndex, questionIndex, index, e.target.value)
                                                                    }
                                                                    placeholder={meta.optionPlaceHolder}
                                                                    className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg
                                                                focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400
                                                                focus:border-transparent bg-white dark:bg-gray-700
                                                            text-gray-900 dark:text-gray-100"
                                                                />

                                                                {/* Delete Button */}
                                                                <Button
                                                                    type="button"
                                                                    onClick={() => handleRemoveOption(moduleIndex, questionIndex, index)}
                                                                    variant="icon"
                                                                    color="danger"
                                                                    size="sm"
                                                                >
                                                                    <DeleteIcon className="w-4 h-4" />
                                                                </Button>
                                                            </div>
                                                        ))}

                                                    </div>
                                                }
                                            </div>
                                        )}
                                        addButtonLabel={meta.addQuestionButton}
                                        emptyMessage={meta.noQuestionMessage}
                                        itemLabel='Question'
                                        startCollapsed={false}
                                    />
                                </div>
                            )}
                            renderHeader={(module, index) => (
                                <>
                                    {getModuleIcon(module.type, "w-5 h-5 text-primary-600 dark:text-primary-400")}
                                    {module.title ? (
                                        <span className="font-medium text-gray-900 dark:text-white">{module.title ? module.title : `Module ${index + 1}`}</span>
                                    ) : (
                                        <span className="font-medium text-gray-400 dark:text-gray-600">Module {index + 1}</span>
                                    )}
                                </>
                            )}
                            addButtonLabel={meta.addModuleButton}
                            emptyMessage={meta.noModulesMessage}
                            itemLabel='Module'
                        />
                    </div>
                </form>
            </Modal >

            {/* Library Browser Modal */}
            {
                showLibraryBrowser && (
                    <Modal
                        isOpen={showLibraryBrowser}
                        onClose={() => {
                            setShowLibraryBrowser(false)
                            setCurrentModuleIndex(null)
                        }}
                        title={meta.libraryBrowserTitle}
                        footer={
                            <div className="flex justify-end gap-3">
                                <Button
                                    type="button"
                                    onClick={() => {
                                        setShowLibraryBrowser(false)
                                        setCurrentModuleIndex(null)
                                    }}
                                    variant="outline"
                                    color="neutral"
                                >
                                    {meta.libraryBrowserCancelButton}
                                </Button>
                            </div>
                        }
                    >
                        <div className="px-6 py-4">
                            {loadingLibrary ? (
                                <div className="text-center py-8 text-gray-600 dark:text-gray-400">
                                    {meta.libraryBrowserLoadingText}
                                </div>
                            ) : error ? (
                                <div className="text-center py-8 text-danger-600 dark:text-danger-400">
                                    {meta.libraryBrowserErrorText}
                                </div>
                            ) : libraryVideos.length === 0 ? (
                                <div className="text-center py-8 text-gray-600 dark:text-gray-400">
                                    {meta.libraryBrowserNoVideosText}
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-96 overflow-y-auto pe-2">
                                    {libraryVideos.map((video: any) => {
                                        const videoId = video.guid || video.id || video.video_id
                                        const thumbnailUrl = video.thumbnailUrl

                                        return (
                                            <div
                                                key={videoId}
                                                className="border border-gray-300 dark:border-gray-600 rounded-lg p-3 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors"
                                                onClick={() => handleSelectLibraryVideo(video)}
                                            >
                                                {thumbnailUrl && (
                                                    <img
                                                        src={thumbnailUrl}
                                                        alt={video.title || 'Video thumbnail'}
                                                        className="w-full h-32 object-cover rounded mb-2"
                                                    />
                                                )}
                                                <div className="flex items-start gap-2">
                                                    <VideoIcon className="w-5 h-5 text-primary-600 dark:text-primary-400 flex-shrink-0 mt-0.5" />
                                                    <div className="flex-1 min-w-0">
                                                        <h3 className="font-medium text-gray-900 dark:text-white truncate">
                                                            {video.title || 'Untitled Video'}
                                                        </h3>
                                                        {video.length && (
                                                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                                                Duration: {Math.ceil(video.length / 60)} min
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            )}
                        </div>
                    </Modal>
                )
            }
        </>
    )
}
